import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Clock3 as ClockIcon, Radar as RadarIcon, LayoutDashboard as DashboardIcon } from "lucide-react";
import "./Values.css";

/*
 * Page Accueil — bénéfices OPENlift, direction premium (audit / maquette 02).
 * Nom du composant conservé pour ne pas toucher à Accueil.jsx.
 * Classes propres à cette section pour éviter le conflit avec ValuesSection.
 * Icônes via lucide-react ; aucune bibliothèque d’animation ajoutée.
 */

const CONTENT = {
  fr: {
    eyebrow: "Les bénéfices au quotidien",
    titleFirst: "Moins de complexité.",
    titleSecond: "Plus de maîtrise.",
    subtitle: "Une plateforme conçue pour simplifier le travail de vos équipes et améliorer le suivi de vos installations.",
    cards: {
      time: {
        title: "Gagnez du temps",
        text: "Accédez plus vite aux bonnes informations pour préparer vos interventions.",
        detail: "Interventions plus efficaces"
      },
      anticipate: {
        title: "Gardez une longueur d'avance",
        text: "Repérez les anomalies et anticipez les actions à mener.",
        detail: "Une vision plus précise"
      },
      manage: {
        title: "Pilotez votre parc",
        text: "Centralisez les informations essentielles et décidez plus rapidement.",
        detail: "Un seul espace de suivi"
      }
    }
  },
  en: {
    eyebrow: "Everyday benefits",
    titleFirst: "Less complexity.",
    titleSecond: "More control.",
    subtitle: "A platform designed to simplify your teams' work and improve visibility across your installations.",
    cards: {
      time: {
        title: "Save time",
        text: "Find the right information faster to prepare your interventions.",
        detail: "More effective interventions"
      },
      anticipate: {
        title: "Stay one step ahead",
        text: "Spot anomalies and anticipate the actions to take.",
        detail: "A clearer view"
      },
      manage: {
        title: "Manage your fleet",
        text: "Bring essential information together and make decisions faster.",
        detail: "One place to stay informed"
      }
    }
  }
};

const BENEFITS = [
  { key: "time", Icon: ClockIcon, count: "01 / 03" },
  { key: "anticipate", Icon: RadarIcon, count: "02 / 03" },
  { key: "manage", Icon: DashboardIcon, count: "03 / 03" }
];

export default function Values() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  // Nouvelles clés isolées : les anciennes traductions Values restent intactes.
  const content = (key, defaultValue) => t(`values.premium.${key}`, { defaultValue });

  const cards = BENEFITS.map(({ key, Icon, count }) => ({
    key,
    Icon,
    count,
    title: content(`cards.${key}.title`, fallback.cards[key].title),
    text: content(`cards.${key}.text`, fallback.cards[key].text),
    detail: content(`cards.${key}.detail`, fallback.cards[key].detail)
  }));

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === "undefined") return undefined;

    const elements = section.querySelectorAll(".ue-benefits-home__card");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("ue-benefits-home__card--revealed");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="ue-benefits-home" aria-labelledby="ue-benefits-home-title">
      <header className="ue-benefits-home__header">
        <p className="ue-benefits-home__eyebrow">
          {content("eyebrow", fallback.eyebrow)}
        </p>
        <h2 className="ue-benefits-home__title" id="ue-benefits-home-title">
          <span>{content("titleFirst", fallback.titleFirst)}</span>
          <span>{content("titleSecond", fallback.titleSecond)}</span>
        </h2>
        <p className="ue-benefits-home__subtitle">
          {content("subtitle", fallback.subtitle)}
        </p>
      </header>

      <div className="ue-benefits-home__band">
        <div className="ue-benefits-home__container">
          <div className="ue-benefits-home__grid">
            {cards.map(({ key, Icon, count, title, text, detail }) => (
              <article className="ue-benefits-home__card" key={key}>
                <div className="ue-benefits-home__card-top">
                  <span className="ue-benefits-home__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="ue-benefits-home__count" aria-hidden="true">
                    {count}
                  </span>
                </div>
                <h3 className="ue-benefits-home__card-title">{title}</h3>
                <p className="ue-benefits-home__card-text">{text}</p>
                <p className="ue-benefits-home__detail">{detail}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
